import React, { useState } from 'react';
import styled from 'styled-components';
import { Position } from '@blueprintjs/core';
import { TbArrowLeft, TbChevronDown } from 'react-icons/tb';
import { Button, DropdownMenu, Flex, Menu, MenuItem, Text, Tooltip } from 'core/components';
import { BreadcrumbOption, BreadcrumbSegment } from '../categoryUtils';

interface MeasurementBreadcrumbProps {
  /**
   * Leading static label. Normally the top-level category (e.g. 'NMS'), or the full
   * formatted path when the measurement can't be resolved into selectable segments.
   */
  categoryLabel: string;
  /** Ordered breadcrumb segments produced by getMeasurementBreadcrumb. May be empty. */
  segments: BreadcrumbSegment[];
  /** Called with the concrete measurement to select when a dropdown option is chosen. */
  onSelectMeasurement: (measurement: string) => void;
  /** Optional reset handler that returns to the category picker. */
  onReset?: () => void;
}

const Separator = styled(Text).attrs({ as: 'span' })`
  flex-shrink: 0;
  color: ${(props) => props.theme.colors.specialMuted};
  font-size: 12px;
  margin: 0px 4px;
`;

const TriggerButton = styled(Button)`
  &.bp4-button {
    min-height: 20px;
    padding: 0 4px;
    font-size: 12px;
    font-weight: 500;
    max-width: 200px;
  }
  .bp4-button-text {
    margin-right: 2px;
  }
`;

const ScrollMenu = styled(Menu)`
  max-height: calc(100vh - 160px);
  overflow: auto;
  scrollbar-width: thin;
`;

/** A single interactive breadcrumb segment that manages its own open state. */
const BreadcrumbDropdown = ({
  segment,
  onSelectMeasurement
}: {
  segment: BreadcrumbSegment;
  onSelectMeasurement: (measurement: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (option: BreadcrumbOption) => {
    setIsOpen(false);
    // Avoid resetting the query when re-selecting the already-active measurement.
    if (option.key !== segment.selectedKey) {
      onSelectMeasurement(option.measurement);
    }
  };

  return (
    <DropdownMenu
      isOpen={isOpen}
      onInteraction={(nextOpenState) => setIsOpen(nextOpenState)}
      position={Position.BOTTOM_LEFT}
      boundary="viewport"
      modifiers={{
        flip: { enabled: true },
        hide: { enabled: false },
        preventOverflow: { enabled: true }
      }}
      usePortal
      canEscapeKeyClose
      content={
        <ScrollMenu p="4px" minWidth="220px">
          {segment.options.map((option) => (
            <MenuItem
              key={option.key}
              text={option.label}
              active={option.key === segment.selectedKey}
              onClick={() => handleSelect(option)}
            />
          ))}
        </ScrollMenu>
      }
    >
      <TriggerButton
        minimal
        small
        ellipsis
        active={isOpen}
        rightIcon={TbChevronDown}
        text={segment.label}
        title={segment.label}
      />
    </DropdownMenu>
  );
};

/**
 * Renders the selected measurement path as an interactive breadcrumb.
 * The category is static text; each path segment that has sibling alternatives
 * becomes a dropdown that lets the user switch the selected measurement.
 */
const MeasurementBreadcrumb = ({
  categoryLabel,
  segments,
  onSelectMeasurement,
  onReset
}: MeasurementBreadcrumbProps) => (
  <Flex className="ude-measurement-breadcrumb" alignItems="center" flexWrap="wrap" minWidth={0}>
    {onReset && (
      <Tooltip content="Back to categories">
        <Button
          minimal
          small
          icon={TbArrowLeft}
          onClick={onReset}
          aria-label="Back to categories"
          style={{ minHeight: 20, marginRight: 2 }}
        />
      </Tooltip>
    )}
    <Tooltip content={categoryLabel} hoverOpenDelay={600}>
      <Text as="div" fontSize={12} fontWeight="bold" minWidth={0} ellipsis>
        {categoryLabel}
      </Text>
    </Tooltip>
    {segments.map((segment, index) => (
      <React.Fragment key={segment.selectedKey || index}>
        <Separator>/</Separator>
        {segment.isDropdown ? (
          <BreadcrumbDropdown segment={segment} onSelectMeasurement={onSelectMeasurement} />
        ) : (
          <Text as="span" fontSize={12} fontWeight="medium" flex="0 0 auto">
            {segment.label}
          </Text>
        )}
      </React.Fragment>
    ))}
  </Flex>
);

export default MeasurementBreadcrumb;
